import {memo, useCallback} from 'react'
import * as Clipboard from 'expo-clipboard'
import {msg} from '@lingui/macro'
import {useLingui} from '@lingui/react'

import {isNative} from '#/platform/detection'
import {type MatrixMessage} from '#/state/matrix/hooks/useMatrixRoom'
import * as Toast from '#/view/com/util/Toast'
import * as ContextMenu from '#/components/ContextMenu'
import {type TriggerProps} from '#/components/ContextMenu/types'
import {Clipboard_Stroke2_Corner2_Rounded as ClipboardIcon} from '#/components/icons/Clipboard'
import {SecureChatEmojiReactionPicker} from './SecureChatEmojiReactionPicker'

export let SecureChatMessageContextMenu = ({
  message,
  myUserId,
  onEmojiSelect,
  children,
}: {
  message: MatrixMessage
  myUserId: string
  onEmojiSelect: (emoji: string) => void
  children: TriggerProps['children']
}): React.ReactNode => {
  const {_} = useLingui()
  const isFromSelf = message.isMe

  const onCopyMessage = useCallback(() => {
    Clipboard.setStringAsync(message.body)
    Toast.show(_(msg`Copied to clipboard`), 'clipboard-check')
  }, [_, message.body])

  return (
    <ContextMenu.Root>
      {isNative && (
        <ContextMenu.AuxiliaryView align={isFromSelf ? 'right' : 'left'}>
          <SecureChatEmojiReactionPicker
            message={message}
            myUserId={myUserId}
            onEmojiSelect={onEmojiSelect}
          />
        </ContextMenu.AuxiliaryView>
      )}

      <ContextMenu.Trigger
        label={_(msg`Message options`)}
        contentLabel={_(
          msg`Message from ${message.senderName}: ${message.body}`,
        )}>
        {children}
      </ContextMenu.Trigger>

      <ContextMenu.Outer align={isFromSelf ? 'right' : 'left'}>
        {message.body.length > 0 && (
          <ContextMenu.Item
            testID="secureChatMessageDropdownCopyBtn"
            label={_(msg`Copy message text`)}
            onPress={onCopyMessage}>
            <ContextMenu.ItemText>
              {_(msg`Copy message text`)}
            </ContextMenu.ItemText>
            <ContextMenu.ItemIcon icon={ClipboardIcon} position="right" />
          </ContextMenu.Item>
        )}
      </ContextMenu.Outer>
    </ContextMenu.Root>
  )
}
SecureChatMessageContextMenu = memo(SecureChatMessageContextMenu)
